import { browserUuidV4 } from "@/lib/browserUuid";
import { commercialImpressionKey, type CommercialInteraction } from "@/lib/commercial-analytics-model";
import { currentSessionAnalyticsAttribution } from "@/lib/analytics-attribution";
export * from "@/lib/commercial-analytics-model";

const inMemoryImpressions = new Set<string>();

export function claimCommercialImpression(interaction: CommercialInteraction) {
  const key = commercialImpressionKey(interaction);
  if (inMemoryImpressions.has(key)) return false;
  try {
    const storageKey = `teravia:commercial-impression:${key}`;
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Memory dedupe remains available when session storage is unavailable.
  }
  inMemoryImpressions.add(key);
  return true;
}

function shouldSkipBrowserTracking() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;
  if (navigator.doNotTrack === "1") return true;
  const host = window.location.hostname.toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "::1" || host.endsWith(".local") || /^192\.168\./.test(host) || /^10\./.test(host);
}

function outboundHost(url: string | null | undefined) {
  try {
    if (!url) return null;
    return new URL(url, window.location.origin).hostname.toLowerCase().replace(/^www\./, "").slice(0, 253) || null;
  } catch {
    return null;
  }
}

function sessionCommercialAttribution() {
  try {
    const attribution = currentSessionAnalyticsAttribution();
    return { source: attribution.source, channel: attribution.channel, campaign: attribution.utmCampaign, tracking_link: attribution.trackingLink };
  } catch {
    return { source: null, channel: null, campaign: null, tracking_link: null };
  }
}

export function dispatchCommercialInteraction(
  eventType: "impression" | "click",
  interaction: CommercialInteraction,
  outboundUrl?: string | null,
) {
  if (shouldSkipBrowserTracking()) return false;
  const body = JSON.stringify({
    client_event_id: browserUuidV4(),
    event_type: eventType,
    ...interaction,
    ...sessionCommercialAttribution(),
    outbound_host: eventType === "click" ? outboundHost(outboundUrl) : null,
  });
  try {
    const sent = navigator.sendBeacon?.(
      "/api/commercial-interactions",
      new Blob([body], { type: "application/json" }),
    );
    if (!sent) {
      void fetch("/api/commercial-interactions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
    return true;
  } catch {
    // Commercial analytics must never block or alter outbound navigation.
    return false;
  }
}

export function trackCommercialImpression(interaction: CommercialInteraction) {
  if (!claimCommercialImpression(interaction)) return false;
  return dispatchCommercialInteraction("impression", interaction);
}

export function trackCommercialClick(interaction: CommercialInteraction, outboundUrl?: string | null) {
  return dispatchCommercialInteraction("click", interaction, outboundUrl);
}
